import { EanLabelType } from '@/models/Ean'
import { Country, Culture, Language } from '@/models/Global'
import { AttributeInput, ProductInput } from '@/models/Import'
import { ImageSource } from '@/models/Presentation'
import { AvailableEanLabelData, LanguageSpecifics, SimplifiedProduct, SimplifiedProductAttribute } from '@/models/Product'
import { RegisteredTaxonomy, SimplifiedAttribute, SimplifiedCategory, SimplifiedTaxonomicObject } from '@/models/Taxonomy'
import { areComparable, arrayInnerJoin, arrayToRecord, removeEmptyObjects, sortLexioNumerically, toComparable } from './tools'
import { PartialRecord } from '@/models/GenericInterfaces'

export const sanitizeProduct = (productInput: ProductInput | undefined) =>
  removeEmptyObjects(productInput, undefined, new Set(['tags', 'collections', 'categories']))

export const getEanLabelDimensions = (type: EanLabelType) => (type === 'sticker-standard' ? '54x30mm' : type === 'sticker-compressed' ? '35x30mm' : '79x18mm')

export const sortCategories = (
  categories?: SimplifiedCategory[],
  sortField: keyof SimplifiedCategory = 'name',
  sortOrder = 'asc' as 'asc' | 'desc',
  sortOrderNumeric = 'asc' as 'asc' | 'desc',
) => sortLexioNumerically(categories, sortField, sortOrder, sortOrderNumeric, 2000)

export const getImageUrl = (imagePath: string | undefined, size = 2560 as 2560 | 1280 | 640 | 256, source?: ImageSource) => {
  if ((!source || source === 'imageLibrary') && imagePath) {
    return `https://imgs.burde.com/media/${size}/${imagePath}`
  }
  return imagePath || ''
}

export const compareAttribute = (attribute: SimplifiedAttribute | AttributeInput, defaultName: string | number, attributeId?: string) =>
  attribute.id && attributeId ? attribute.id === attributeId : areComparable(attribute.defaultName, defaultName)

export const getProductAttribute = <T extends SimplifiedProduct | ProductInput>(product: T | undefined, defaultName: string, attributeId?: string) =>
  product?.attributes?.find((attribute) => compareAttribute(attribute, defaultName, attributeId)) as
    | (T extends SimplifiedProduct ? SimplifiedProductAttribute : AttributeInput)
    | undefined

export const getProductAttributeValuesString = (product: SimplifiedProduct, defaultName: string, key = 'name' as 'name' | 'defaultName') => {
  const productAttribute = getProductAttribute(product, defaultName)
  return getValueStringFromAttribute(productAttribute, key)
}

export const getValueStringFromAttribute = (productAttribute: SimplifiedProductAttribute | undefined, key = 'name' as 'name' | 'defaultName') =>
  arrayInnerJoin(productAttribute?.options, key)

export const getFallbackStringFromAttribute = (productAttribute: SimplifiedProductAttribute | undefined, key = 'name' as 'name' | 'defaultName') =>
  arrayInnerJoin(productAttribute?.standardOptions, key)

export const getProductTaxonomicValueString = (product: SimplifiedProduct, taxonomy: RegisteredTaxonomy, key = 'name' as 'name' | 'defaultName') =>
  arrayInnerJoin(product[taxonomy], key)

export const productHasCulturalData = (product: SimplifiedProduct | undefined, culture: Culture) => {
  const languageDataExists = product?.languageSpecific?.[culture.language]
  const countryDataExists = product?.countrySpecific?.[culture.country]
  return !!(languageDataExists || countryDataExists)
}

export const getProductAttributesByComparable = (product: SimplifiedProduct) =>
  arrayToRecord<SimplifiedProductAttribute>(product.attributes, 'defaultName', toComparable)

export const getProductAttributeNameValuePair = (product: SimplifiedProduct, defaultName: string): string | undefined => {
  const attribute = getProductAttribute(product, defaultName)
  return attribute?.options?.length ? `${attribute.name}: ${arrayInnerJoin(attribute.options, 'name')}` : undefined
}

export const countryDoesNotExclude = (exclusiveToCountries: Array<Country | 'global'> | undefined, country: Country) =>
  !exclusiveToCountries || exclusiveToCountries.includes(country)

export const getAltLanguageField = (product: ProductInput | SimplifiedProduct, field: keyof LanguageSpecifics) => {
  for (const language in product.languageSpecific) {
    const languageData = product.languageSpecific[language as Language]
    const value = languageData?.[field]

    if (value && typeof value !== 'object') {
      return { language: language as Language, value }
    }
  }
}

export const eanLabelAvailableFor = (availableEanLabelData: AvailableEanLabelData, country: Country) => {
  const explicitCountry = availableEanLabelData.explicitCulture?.country

  return availableEanLabelData?.availableInCountries?.[explicitCountry || country]
  // return availableEanLabelData?.isInternational
  //   ? explicitCountry && availableEanLabelData?.availableInCountries?.[explicitCountry]
  //   : availableEanLabelData?.availableInCountries?.[explicitCountry || country]
}
export const getMainName = (product: SimplifiedProduct | undefined) => product?.computed?.['main name']?.options?.[0]
